import { AppUser, Property, UserRole } from '../../types';
import { TechnicalSheetData, SheetImage, SheetSpecItem, SheetPointOfInterest, SheetBranding } from './types';
import { formatCurrency } from '../../utils/formatters';

export const adaptPropertyToTechnicalSheet = (
  property: Property,
  appUser?: AppUser | null
): TechnicalSheetData => {
  const rawImages = (property.images || []).filter(Boolean);
  const validImages: SheetImage[] = rawImages.map((url, idx) => ({
    url,
    isMain: idx === 0,
  }));

  const city = property.ciudad || 'Bogotá D.C.';
  const neighborhood = property.neighborhoodName || property.localidad || 'Sector Exclusivo';
  const fullAddress = [property.direccion, neighborhood, city, 'Colombia'].filter(Boolean).join(' • ');

  const calculatedAge = property.constructionYear
    ? `${new Date().getFullYear() - property.constructionYear} años`
    : property.antiguedad ? `${property.antiguedad} años` : 'A consultar';

  const specs: SheetSpecItem[] = [
    { label: 'Antigüedad', value: calculatedAge },
    { label: 'Niveles / Pisos', value: property.piso ? `Piso ${property.piso}` : '3 niveles' },
    { label: 'Disposición', value: 'Exterior con excelente iluminación' },
    ...(property.ascensor !== undefined && property.ascensor !== null
      ? [{
          label: 'Ascensor',
          value: Number(property.ascensor) > 1
            ? `${property.ascensor} ascensores`
            : Number(property.ascensor) === 1
            ? 'Sí'
            : 'No',
        }]
      : []),
    { label: 'Área Construida', value: property.area_construida_original || '220 m²' },
    { label: 'Área Privada', value: property.area_privada || property.area_construida_original || '200 m²' },
    { label: 'Estrato', value: property.estrato ? String(property.estrato) : '4' },
    {
      label: 'Administración',
      value: property.valor_administracion && property.valor_administracion > 0
        ? formatCurrency(property.valor_administracion)
        : 'Sin costo / No aplica',
    },
  ];

  const surroundings: SheetPointOfInterest[] = [
    { name: 'Estación TransMilenio más cercana', distance: '350 m' },
    { name: 'Parque principal y zona gastronómica', distance: '120 m' },
  ];

  const highlights = (property.features && property.features.length > 0)
    ? property.features.slice(0, 4).map((f) => ({ text: f }))
    : [{ text: 'Estructura sólida con acabados de alta calidad' }];

  const amenities = property.features?.slice(4, 12) || ['Patio interior privado', 'Cocina amplia'];

  // User role profile determination
  const roleStr = String(appUser?.role || '').trim().toUpperCase();
  const sourceDB = String(appUser?.sourceDB || '').trim().toLowerCase();
  
  const isClient = roleStr === 'CLIENTE' || roleStr === 'CLIENT';
  const isBrokerOrPremium =
    roleStr === UserRole.BROKER ||
    roleStr === UserRole.BROKER_PREMIUM ||
    roleStr === 'BROKER' ||
    roleStr === 'BROKER_PREMIUM' ||
    (sourceDB === 'broker' && !roleStr.includes('CLIENTE'));

  const showAdvisorSection = !isClient;
  const showAdvisorContactInfo = isBrokerOrPremium;
  const isBroker = isBrokerOrPremium || roleStr.includes('BROKER');
  const isPremium = roleStr.includes('PREMIUM');

  const defaultAdvisorTitle = isPremium
    ? 'Broker Inmobiliario Premium'
    : isBroker ? 'Broker Inmobiliario Aliado' : 'Especialista Inmobiliario';

  const userName = appUser?.nombreCompleto || appUser?.displayName || (isBroker ? 'Broker Inmobiliario' : 'Asesor Comercial');
  const nameParts = userName.trim().split(' ');
  const initials = nameParts.length >= 2
    ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
    : (nameParts[0]?.slice(0, 2) || (isBroker ? 'BR' : 'CC')).toUpperCase();

  const branding: SheetBranding = isBroker
    ? {
        profileType: 'broker',
        headerTitle: userName,
        headerSubtitle: isBrokerOrPremium ? (appUser?.email || 'contacto@broker.com') : 'Portal Inmobiliario Ciencuadras',
        headerSubtitleIsLink: false,
        brokerInitials: initials,
      }
    : {
        profileType: 'comercial',
        headerTitle: 'Portal Inmobiliario Ciencuadras',
        headerSubtitle: 'www.ciencuadras.com',
        headerSubtitleIsLink: true,
        headerSubtitleHref: 'https://www.ciencuadras.com',
        footerBrandText: 'Portal Inmobiliario Ciencuadras',
      };

  return {
    operationType: 'VENTA',
    propertyType: (property.tipoinmueble || 'CASA').toUpperCase(),
    title: property.tipoinmueble ? `${property.tipoinmueble} en ${neighborhood}` : `Inmueble en ${neighborhood}`,
    address: fullAddress,
    referenceCode: property.codigo || '183150-23461922505',
    priceFormatted: formatCurrency(property.precio_venta || 0),
    adminFeeFormatted: property.valor_administracion ? formatCurrency(property.valor_administracion) : undefined,
    mainBadge: property.estrategiaComercial || 'BIEN DESTACADO',
    images: validImages,
    metrics: {
      area: property.area_construida_original || '220 m²',
      rooms: property.num_habitaciones ?? 4,
      baths: property.num_banos ?? 3,
      parking: property.num_parqueaderos ?? 2,
    },
    description: property.descripcion || 'Inmueble con distribución armónica, amplios niveles y generosa iluminación natural en sector privilegiado.',
    highlights,
    amenities,
    specs,
    surroundings,
    advisor: {
      name: userName,
      initials,
      title: defaultAdvisorTitle,
      phone: showAdvisorContactInfo ? (appUser?.celular || '+57 (300) 123-4567') : undefined,
      email: showAdvisorContactInfo ? (appUser?.email || 'contacto@broker.com') : undefined,
      agencyName: (appUser as any)?.inmobiliaria || (isBroker ? 'Red de Brokers Aliados' : 'Ciencuadras'),
      location: `${neighborhood}, ${city}`,
      showContactInfo: showAdvisorContactInfo,
    },
    showAdvisorSection,
    showAdvisorContactInfo,
    location: {
      latitude: property.latitude ?? undefined,
      longitude: property.longitude ?? undefined,
      address: property.direccion || fullAddress,
      neighborhood,
      city,
    },
    branding,
    footerCode: property.codigo || '183150-23461922505',
    pageNumber: 1,
    totalPages: 1,
  };
};
